// Shared car parts: materials, tyres with a colored sidewall band, rims,
// mirrors, the number decal, livery textures.
import * as THREE from 'three'
import { paintLivery, paintTrim, type Livery, type Placement } from './livery'

// Glossy race paint with a clear coat.
export function paintMaterial(color: THREE.ColorRepresentation | null, map?: THREE.Texture) {
  return new THREE.MeshPhysicalMaterial({
    color: color ?? 0xffffff, map: map ?? null, roughness: 0.32, metalness: 0.18, clearcoat: 1, clearcoatRoughness: 0.08,
  })
}

export const materials = {
  carbon: new THREE.MeshStandardMaterial({ color: 0x18191c, roughness: 0.42, metalness: 0.35 }),
  carbonMatte: new THREE.MeshStandardMaterial({ color: 0x111214, roughness: 0.8, metalness: 0.1 }),
  rubber: new THREE.MeshStandardMaterial({ color: 0x141416, roughness: 0.92, side: THREE.DoubleSide }),
  rim: new THREE.MeshStandardMaterial({ color: 0x6b6f78, roughness: 0.25, metalness: 0.9 }),
  chrome: new THREE.MeshStandardMaterial({ color: 0xc9ccd2, roughness: 0.18, metalness: 1 }),
  glass: new THREE.MeshPhysicalMaterial({ color: 0x0c1218, roughness: 0.05, metalness: 0.2, clearcoat: 1 }),
  dark: new THREE.MeshStandardMaterial({ color: 0x050506, roughness: 0.9 }),
  tail: new THREE.MeshBasicMaterial({ color: 0xff2020 }),
  grille: new THREE.MeshStandardMaterial({ color: 0x0b0b0c, roughness: 0.6, metalness: 0.4 }),
}

export function liveryTexture(livery: Livery, placement: Placement | null): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  if (placement) paintLivery(canvas, livery, placement)
  else paintTrim(canvas, livery)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 8
  return texture
}

// Sidewall band: the compound ring and lettering (like a soft/medium stripe).
const sidewallCache = new Map<string, THREE.CanvasTexture>()
function sidewallTexture(color: string): THREE.CanvasTexture {
  const cached = sidewallCache.get(color)
  if (cached) return cached
  const size = 512
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const x = canvas.getContext('2d')!
  const c = size / 2
  x.fillStyle = '#161618'
  x.fillRect(0, 0, size, size)
  x.strokeStyle = color
  x.lineWidth = size * 0.035
  x.beginPath()
  x.arc(c, c, size * 0.405, 0, Math.PI * 2)
  x.stroke()
  x.fillStyle = '#E8E8E4'
  x.font = `800 ${size * 0.05}px "Saira Condensed", "Arial Narrow", sans-serif`
  x.textAlign = 'center'
  x.textBaseline = 'middle'
  for (const [word, angle] of [['LAUNCHGP', -Math.PI / 2], ['RACING', Math.PI / 2], ['LGP', 0], ['LGP', Math.PI]] as const) {
    x.save()
    x.translate(c, c)
    x.rotate(angle + Math.PI / 2)
    x.fillText(word, 0, -size * 0.455)
    x.restore()
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  sidewallCache.set(color, texture)
  return texture
}

export type WheelSpec = { radius: number; width: number; rimRadius: number; spokes: number; band: string; rimMaterial?: THREE.Material; cover?: THREE.Material }

// A wheel whose axle is the x axis. Returns a pivot (place it at the hub) and
// an inner group that spins.
const tyreGeometryCache = new Map<string, THREE.BufferGeometry>()
export function wheel(spec: WheelSpec, side: 1 | -1): { pivot: THREE.Group; spin: THREE.Group } {
  const pivot = new THREE.Group()
  const spin = new THREE.Group()
  pivot.add(spin)
  spin.userData.wheel = true
  const { radius: R, width: w, rimRadius: r } = spec
  const key = `${R}-${w}-${r}`
  let tyreGeo = tyreGeometryCache.get(key)
  if (!tyreGeo) {
    // Tyre profile (radius, axial) swept around the axle: flat tread,
    // rounded shoulders, bulging sidewalls down to the rim.
    const s = Math.min(0.07, w * 0.22)
    const half = w / 2
    const pts: THREE.Vector2[] = [new THREE.Vector2(r, -half * 0.9), new THREE.Vector2(r + (R - r) * 0.5, -half * 1.03)]
    for (let i = 0; i <= 6; i++) {
      const t = (i / 6) * (Math.PI / 2)
      pts.push(new THREE.Vector2(R - s + s * Math.sin(t), -half + s - s * Math.cos(t)))
    }
    for (let i = 0; i <= 6; i++) {
      const t = (i / 6) * (Math.PI / 2)
      pts.push(new THREE.Vector2(R - s + s * Math.cos(t), half - s + s * Math.sin(t)))
    }
    pts.push(new THREE.Vector2(r + (R - r) * 0.5, half * 1.03), new THREE.Vector2(r, half * 0.9))
    tyreGeo = new THREE.LatheGeometry(pts, 48)
    tyreGeo.rotateZ(Math.PI / 2)
    tyreGeometryCache.set(key, tyreGeo)
  }
  const tyre = new THREE.Mesh(tyreGeo, materials.rubber)
  tyre.castShadow = true
  spin.add(tyre)

  // Sidewall bands, both faces.
  const band = new THREE.MeshStandardMaterial({ map: sidewallTexture(spec.band), roughness: 0.85, polygonOffset: true, polygonOffsetFactor: -1 })
  for (const face of [1, -1]) {
    const ring = new THREE.Mesh(new THREE.RingGeometry(r * 1.02, R * 0.97, 64, 1), band)
    ring.rotation.y = face * Math.PI / 2
    ring.position.x = face * (w / 2 + 0.004)
    // Planar UVs of RingGeometry already map the square canvas.
    spin.add(ring)
  }

  // Rim (outer face only matters) with spokes, lip and centre lock.
  const rimMat = spec.rimMaterial ?? materials.rim
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(r, r, w * 0.86, 36, 1, true), materials.carbonMatte)
  barrel.rotation.z = Math.PI / 2
  spin.add(barrel)
  const outer = side * (w / 2 - 0.02)
  const lip = new THREE.Mesh(new THREE.TorusGeometry(r * 0.98, 0.012, 8, 48), rimMat)
  lip.rotation.y = Math.PI / 2
  lip.position.x = outer
  spin.add(lip)
  const dish = new THREE.Mesh(new THREE.CircleGeometry(r * 0.96, 36), materials.carbonMatte)
  dish.rotation.y = side * Math.PI / 2
  dish.position.x = outer - side * 0.05
  spin.add(dish)
  if (spec.cover) {
    const cover = new THREE.Mesh(new THREE.CircleGeometry(r * 0.94, 40), spec.cover)
    cover.rotation.y = side * Math.PI / 2
    cover.position.x = outer + side * 0.002
    spin.add(cover)
  } else {
    for (let i = 0; i < spec.spokes; i++) {
      const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.025, r * 0.78, 0.028), rimMat)
      const a = (i / spec.spokes) * Math.PI * 2
      spoke.position.set(outer - side * 0.02, Math.cos(a) * r * 0.5, Math.sin(a) * r * 0.5)
      spoke.rotation.x = a
      spin.add(spoke)
    }
  }
  const hub = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.2, r * 0.22, 0.06, 6), rimMat)
  hub.rotation.z = Math.PI / 2
  hub.position.x = outer
  spin.add(hub)
  const nut = new THREE.Mesh(new THREE.CylinderGeometry(r * 0.09, r * 0.09, 0.08, 6), materials.chrome)
  nut.rotation.z = Math.PI / 2
  nut.position.x = outer + side * 0.03
  spin.add(nut)
  return { pivot, spin }
}

// Number decal (white figures with a dark outline) for flat spots like a nose.
export function numberDecal(number: number | string, size: number, color = '#F7F7F5'): THREE.Mesh {
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 256
  const x = canvas.getContext('2d')!
  x.textAlign = 'center'
  x.textBaseline = 'middle'
  x.font = 'italic 800 190px "Saira Condensed", "Arial Narrow", sans-serif'
  x.lineWidth = 14
  x.strokeStyle = '#0E0E0E'
  x.strokeText(String(number), 128, 138)
  x.fillStyle = color
  x.fillText(String(number), 128, 138)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return new THREE.Mesh(
    new THREE.PlaneGeometry(size, size),
    new THREE.MeshStandardMaterial({ map: texture, transparent: true, roughness: 0.3, polygonOffset: true, polygonOffsetFactor: -4 }),
  )
}

export function mirror(paint: THREE.Material, stalkFrom: THREE.Vector3, at: THREE.Vector3, size: [number, number, number]): THREE.Group {
  const group = new THREE.Group()
  const stalk = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, stalkFrom.distanceTo(at), 6), materials.carbon)
  stalk.position.copy(stalkFrom).lerp(at, 0.5)
  stalk.lookAt(at)
  stalk.rotateX(Math.PI / 2)
  const housing = new THREE.Mesh(new THREE.SphereGeometry(1, 16, 10), paint)
  housing.scale.set(size[0] / 2, size[1] / 2, size[2] / 2)
  housing.position.copy(at)
  housing.castShadow = true
  const glass = new THREE.Mesh(new THREE.CircleGeometry(1, 16), materials.chrome)
  glass.scale.set(size[0] * 0.42, size[1] * 0.38, 1)
  glass.position.copy(at).add(new THREE.Vector3(0, 0, -size[2] / 2 - 0.002))
  glass.rotation.y = Math.PI
  group.add(stalk, housing, glass)
  return group
}
